<template>
  <!--
  IMPORTANT!
  A fork from the repository is used.
  Many event listeners have been disabled in the fork for optimization purposes.

  Source repo: https://github.com/Happy-Coding-Clans/vue-easytable
  Forked repo: https://github.com/cardpr/vue-easytable
-->

  <div id="loading-container" class="wrapper">
    <ve-table :columns="columns" :table-data="nonReactiveRows" v-bind="options" />

    <div v-if="!isLoading && total <= 0" class="empty">Данных нет</div>

    <div v-if="paginationOptions.show" class="pagination-wrapper">
      <ve-pagination v-bind="paginationOptions" v-on="paginationListeners" />

      <OpenSettingsButton :show.sync="isShownModal" />
    </div>

    <ColumnsSettingsModal
      v-if="isShownModal"
      :source-columns="sourceColumns"
      :show.sync="isShownModal"
      :storage-id="storageId"
      @update-columns="updateColumns"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import syncQuery from '@/mixins/syncUrlQuery'
import VueEasytable from 'vue-easytable'

import ruRU from './lang/ru-RU.js'

import ColumnsSettingsMethods from './columnsSettings/ColumnsSettingsMethods'

Vue.use(VueEasytable)

/** Table component  */
export default {
  components: {
    OpenSettingsButton: () => import('./columnsSettings/OpenSettingsButton.vue'),
    ColumnsSettingsModal: () => import('./columnsSettings/ColumnsSettingsModal.vue'),
  },
  mixins: [syncQuery, ColumnsSettingsMethods],
  props: {
    storageId: {
      type: String,
      default: '',
      required: true,
    },
    /** Array of column objects */
    sourceColumns: {
      type: Array,
      default: () => [],
    },
    /** Array of rows objects */
    rows: {
      type: Array,
      default: () => [],
    },
    /** Options for <ve-table /> component. https://happy-coding-clans.github.io/vue-easytable/#/en/doc/table/usage */
    tableProps: {
      type: Object,
      default: () => ({}),
    },
    /** Options for <ve-pagination /> component. https://happy-coding-clans.github.io/vue-easytable/#/en/doc/base/pagination */
    pagination: {
      type: Object,
      default: () => ({}),
    },
    /** Options sorting. https://happy-coding-clans.github.io/vue-easytable/#/en/doc/table/header-sort */
    sortProps: {
      type: Object,
      default: () => ({}),
    },
    /** Loading table flag. https://happy-coding-clans.github.io/vue-easytable/#/en/doc/base/loading */
    isLoading: {
      type: Boolean,
      default: false,
    },
    /** Count of total records */
    total: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isShownModal: false,
      columns: [],
    }
  },
  computed: {
    /** Primitive property for watch query value without `deep` watching. */
    stringfyString() {
      return JSON.stringify(this.$query.sort)
    },

    /** Mock `this.rows` observer for optimization. */
    nonReactiveRows() {
      if (this.rows && this.rows.length <= 0) return []

      return Vue.nonreactive(this.rows)
    },

    /** Merged incoming options with default options for <ve-table /> component. */
    tableOptions() {
      const defaults = {
        'row-style-option': { stripe: true, hoverHighlight: false, clickHighlight: false },
        'border-around': false,
        'border-x': false,
      }

      return Object.assign({}, defaults, this.tableProps)
    },

    /** Merged incoming options with default options for <ve-table /> component. */
    sortOptions() {
      const defaults = {
        multipleSort: false,
        sortAlways: false,
        sortChange: (params) => {
          this.$emit('sort-change', params)
        },
      }

      return {
        'sort-option': Object.assign({}, defaults, this.sortProps),
      }
    },

    /** Merged incoming options with default options for <ve-pagination /> component. */
    paginationOptions() {
      const defaults = {
        show: true,
        total: this.total,
        'page-index': this.page,
        'page-size': this.pageSize,
        'page-size-option': [10, 30, 50, 100],
        layout: ['prev', 'next', 'pager', 'sizer'],
      }

      return Object.assign({}, defaults, this.pagination)
    },

    /** Listeners for <ve-pagination /> component. */
    paginationListeners() {
      return {
        'on-page-number-change': (page) => this.$emit('on-page-number-change', page),
        'on-page-size-change': (size) => this.$emit('on-page-size-change', size),
      }
    },

    /** Total merged `tableOptions` with `sortOptions` for <ve-table /> component. */
    options() {
      return Object.assign({}, this.tableOptions, this.sortOptions)
    },
  },
  watch: {
    stringfyString: {
      handler: 'updateSortBy',
    },
    isLoading: {
      handler: function (value) {
        if (value) this.showLoading()
        else this.closeLoading()
      },
    },
  },
  mounted() {
    this.updateColumns(this.getVisbleColumns(this.setColVisible()))

    this.$veLocale.use(ruRU)

    this.loadingInstance = this.$veLoading({
      target: document.querySelector('#loading-container'),
      name: 'bounce',
    })
    this.showLoading()
  },
  destroyed() {
    this.loadingInstance.destroy()
  },
  methods: {
    /**
     * Update sort icon state in table header.
     *
     * @returns { Void }
     */
    updateSortBy() {
      for (let i = 0; i < this.columns.length; i++) {
        const sortBy = this.sortBy(this.columns[i].field)

        this.$set(this.columns, i, { ...this.columns[i], sortBy })
      }
    },

    /** Show loading table state. */
    showLoading() {
      this.loadingInstance.show()
    },

    /** Hide loading table state. */
    closeLoading() {
      this.loadingInstance.close()
    },

    /**
     * Update columns Array.
     *
     * @param columns {Array} Update columns array.
     *
     * @returns {Void} Set this.columns by provided updated columns.
     */
    updateColumns(columns) {
      this.columns = columns
    },
  },
}
</script>

<style lang="scss">
@import './theme-default/index.scss';
</style>

<style lang="scss" scoped>
.wrapper {
  width: 100%;
  padding: 0 rem(16px) rem(16px) rem(16px);
  background: $color-gray-10;

  .empty {
    width: 100%;
    height: 50px;
    font-size: rem(14px);
    color: $color-gray-400;
    background: $white;

    @include createFlex(flexbox, nowrap, row, center, center, center);
  }

  .pagination-wrapper {
    @include createFlex(flexbox, nowrap, row, space-between, center, center);
  }
}
</style>
